/**
 * ==================== 类型降级兼容模块 ====================
 *
 * 处理未注册的自定义画布对象类型的降级与还原，确保跨分支数据兼容。
 * 同时提供 Fabric.js 内置类型的查找工具函数，解决小写类型名无法匹配构造函数的问题。
 *
 * 【背景】
 * 1. Fabric.js 内置类型名是小写（如 'group'、'rect'），但注册在 fabric 对象上的类名是
 *    首字母大写（如 fabric.Group、fabric.Rect）。直接用 fabric[objType] 查找会匹配失败，
 *    导致 group、rect、path 等标准类型被误判为"未注册"。
 * 2. feature/psd 分支新增了 PSD 图层类型，但其他分支尚未合并该功能。
 *    测试服数据可能包含未注册类型对象（如 psd、占位符），直接加载会报错。
 *
 * 【策略】
 * - 内置类型：通过 getFabricClass 兼容小写/大写查找，无需降级
 * - 自定义未注册类型：降级为兼容类型（image/group），标记原始类型
 * - 保存时：还原降级对象的原始类型，确保数据不丢失
 *
 * 【处理场景】
 * | 场景               | 原始类型 | 处理方式 | 说明           |
 * |--------------------|----------|----------|----------------|
 * | Fabric.js 内置类型 | group/rect/path/... | 直接通过 getFabricClass 查找 | 无需降级 |
 * | PSD 图层（有封面）  | psd      | 降级为 image | 显示封面图片   |
 * | PSD 图层（无封面）  | psd      | 降级为 group | 保留数据结构   |
 * | 生图中的占位符      | 任意     | 降级为 group | 保留占位符状态 |
 * | 上传中的占位符      | 任意     | 降级为 group | 保留占位符状态 |
 * | PSD 占位符         | psd      | 降级为 group | 保留占位符状态 |
 * | 其他未注册类型      | 任意     | 降级为 group | 兜底保留数据   |
 */

import { fabric } from 'fabric';
import { Environment } from "@/constants/env";

const isDevOrTest = import.meta.env.VITE_ENV === Environment.development || import.meta.env.VITE_ENV === Environment.test;

// ==================== 类型定义 ====================

/** 降级目标类型 */
export type DowngradeTargetType = 'image' | 'group';

/** 降级结果 */
export interface DowngradeResult {
  /** 降级后的对象数据 */
  downgraded: any;
  /** 降级目标类型 */
  targetType: DowngradeTargetType;
}

// ==================== 常量 ====================

/** 标记字段：对象已被降级 */
export const DOWNGRADED_FLAG = '_downgraded' as const;

/** 标记字段：原始类型 */
export const ORIGINAL_TYPE_FIELD = '_originalType' as const;

// ==================== Fabric 类型查找 ====================

/**
 * 获取 Fabric.js 的构造函数（兼容小写/大写类型名）
 *
 * Fabric.js 内置类型名是小写（如 'group'、'rect'），但注册在 fabric 对象上的
 * 类名是首字母大写（如 fabric.Group、fabric.Rect）。
 * 此函数统一处理这两种格式，确保都能找到对应的构造函数。
 *
 * @param type 对象的 type 属性值（如 'group'、'Rect'）
 * @returns 对应的 Fabric 构造函数，未找到时返回 null
 *
 * @example
 * getFabricClass('group')   // → fabric.Group
 * getFabricClass('Group')   // → fabric.Group
 * getFabricClass('rect')    // → fabric.Rect
 * getFabricClass('image')   // → fabric.Image
 * getFabricClass('unknown') // → null
 */
export const getFabricClass = (type: string): any => {
  if (!type) return null;

  // 1. 先尝试直接用原始类型名查找（可能已经是正确格式，如 'Image'、'Video'）
  const direct = (fabric as any)[type];
  if (direct) return direct;

  // 2. 首字母大写后再尝试（处理 'group' → 'Group', 'rect' → 'Rect' 等）
  const capitalized = type.charAt(0).toUpperCase() + type.slice(1);
  const capitalizedClass = (fabric as any)[capitalized];
  if (capitalizedClass) return capitalizedClass;

  return null;
};

// ==================== 降级逻辑 ====================

/**
 * 从 PSD 元素的 extraData 字段中提取封面图地址
 *
 * 新版 PSD 元素的封面图可能不再直接存在 src 中，而是存在 extraData.psdPreviewUrl 里。
 * extraData 格式为 JSON 字符串：{ v: 1, psdPreviewUrl: "...", psdDownloadUrl: "...", ... }
 *
 * @param extraData PSD 元素的 extraData 字段
 * @returns 封面图地址，解析失败时返回空字符串
 */
export const extractPsdPreviewUrl = (extraData: unknown): string => {
  if (typeof extraData !== 'string' || !extraData) return '';
  try {
    const parsed = JSON.parse(extraData);
    return parsed?.psdPreviewUrl || '';
  } catch {
    return '';
  }
};

/**
 * 尝试将未注册的对象类型降级为兼容类型
 *
 * @param obj 原始对象数据
 * @returns 降级结果，无法降级时返回 null
 */
export const tryDowngradeObject = (obj: any): DowngradeResult | null => {
  const objType = obj?.type;

  // 占位符对象：直接降级为 group（占位符都是 fabric.Group）
  if (obj?.isPlaceholder) {
    if (isDevOrTest) {
      console.info(
        `[typeDowngrade] 占位符对象类型 "${objType}" 未注册，降级为 group 保留数据`
      );
    }
    return {
      downgraded: {
        ...obj,
        type: 'group',
        [ORIGINAL_TYPE_FIELD]: objType,
        [DOWNGRADED_FLAG]: true,
      },
      targetType: 'group',
    };
  }

  // PSD 类型：尝试从 src 或 extraData 中提取封面图，降级为普通图片显示
  if (objType === 'psd') {
    // 优先使用 src，其次从 extraData 解析 psdPreviewUrl（新版 PSD 元素封面图可能存在 extraData 中）
    const coverSrc = obj.src || extractPsdPreviewUrl(obj.extraData);

    if (coverSrc) {
      if (isDevOrTest) {
        console.info(
          `[typeDowngrade] PSD 类型未注册，降级为普通图片显示（封面来源: ${obj.src ? 'src' : 'extraData.psdPreviewUrl'}）`
        );
      }
      return {
        downgraded: {
          ...obj,
          type: 'image',
          src: coverSrc,
          [ORIGINAL_TYPE_FIELD]: 'psd',
          [DOWNGRADED_FLAG]: true,
        },
        targetType: 'image',
      };
    }

    // 无任何封面图：降级为 group 保留数据结构
    if (isDevOrTest) {
      console.info(
        `[typeDowngrade] PSD 类型未注册且无封面图（src/extraData 均为空），降级为 group 保留数据`
      );
    }
    return {
      downgraded: {
        ...obj,
        type: 'group',
        [ORIGINAL_TYPE_FIELD]: 'psd',
        [DOWNGRADED_FLAG]: true,
      },
      targetType: 'group',
    };
  }

  // 其他未注册的自定义类型：降级为 group 兜底保留数据（防止对象丢失）
  if (objType) {
    if (isDevOrTest) {
      console.info(
        `[typeDowngrade] 未知自定义类型 "${objType}" 未注册，降级为 group 兜底保留数据`
      );
    }
    return {
      downgraded: {
        ...obj,
        type: 'group',
        [ORIGINAL_TYPE_FIELD]: objType,
        [DOWNGRADED_FLAG]: true,
      },
      targetType: 'group',
    };
  }

  // 无类型信息：无法降级
  return null;
};

// ==================== 还原逻辑 ====================

/**
 * 还原降级对象的原始类型（保存时调用）
 *
 * @param obj 对象数据
 * @returns 是否执行了还原
 */
export const restoreDowngradedType = (obj: any): boolean => {
  if (!obj?.[DOWNGRADED_FLAG] || !obj?.[ORIGINAL_TYPE_FIELD]) {
    return false;
  }

  const originalType = obj[ORIGINAL_TYPE_FIELD];
  obj.type = originalType;
  delete obj[DOWNGRADED_FLAG];
  delete obj[ORIGINAL_TYPE_FIELD];

  if (isDevOrTest) {
    console.info(`[typeDowngrade] 还原降级对象为原始类型: ${originalType}`);
  }
  return true;
};

/**
 * 批量还原降级对象（用于保存流程）
 *
 * @param objects 对象数组
 * @returns 处理后的对象数组（原地修改）
 */
export const restoreDowngradedObjects = (objects: any[]): any[] => {
  for (const obj of objects) {
    restoreDowngradedType(obj);
  }
  return objects;
};
